import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { BackpackIcon } from '@/src/renderer/components/icons/BackpackIcon'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import type { UsagePurpose } from '@/src/renderer/lib/supabase/types'
import type { UsagePurposeOption } from './SetupPurposePage.types'
import { CardKeysHint } from '../../../components/CardKeysHint'
import './SetupPurposePage.scss'

/**
 * Available usage purpose options for the purpose
 * Each option includes an emoji, label translation key, and background color
 */
const USAGE_PURPOSE_OPTIONS: UsagePurposeOption[] = [
  { id: 'personal', emoji: '🏡️', labelKey: 'setupPurpose.personal', bgColor: '#D6E7AF' },
  { id: 'small_business', emoji: '🛠️', labelKey: 'setupPurpose.smallBusiness', bgColor: '#FFE4B5' },
  { id: 'large_enterprise', emoji: '🏢', labelKey: 'setupPurpose.largeEnterprise', bgColor: '#E0E7FF' },
  { id: 'teaching', emoji: '📚', labelKey: 'setupPurpose.teaching', bgColor: '#FFE4E6' },
  { id: 'study_learning', emoji: '🎓', labelKey: 'setupPurpose.studyLearning', bgColor: '#DBEAFE' },
  { id: 'nonprofit_charity', emoji: '❤️', labelKey: 'setupPurpose.nonprofit', bgColor: '#FCE7F3' },
]

/**
 * SetupPurposePage Component
 *
 * Onboarding purpose that asks users about their intended usage of HappyTask.
 * Displays a 2x3 grid of selectable purpose cards with full keyboard navigation support.
 *
 * @remarks
 * Features:
 * - Multi-select cards (0-6 selections allowed)
 * - Full keyboard navigation (Arrow keys, Space, Enter, ESC)
 * - Dynamic prompt text showing selection count
 * - Auto-focus on first card
 * - Saves selections to Supabase database
 * - Skip button (ESC) moves on without saving
 *
 * Navigation Flow:
 * - Previous: Profile Setup Page
 * - Next: Home Page
 *
 * Keyboard Controls:
 * - Arrow Left/Right: Navigate horizontally through cards
 * - Arrow Up/Down: Navigate vertically through cards (2-column grid)
 * - Space: Toggle card selection
 * - Enter: Continue to home page
 * - ESC (anywhere on the page): Skip purpose and go to the intent page
 *
 * @example
 * ```tsx
 * <Route path="/setup-purpose" element={
 *   <ProtectedRoute>
 *     <SetupPurposePage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export default function SetupPurposePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const account = useAccountSource()
  const location = useLocation()
  const platformAPI = getPlatformAPI()

  // Get saved data from navigation state (if coming back from intent page or profile page)
  const locationState = location.state as {
    savedPurposes?: UsagePurpose[]
    savedIntents?: any[]
    profileData?: { fullName: string; password: string }
  }
  const savedPurposesFromState = locationState?.savedPurposes
  const savedIntentsFromState = locationState?.savedIntents
  const profileDataFromState = locationState?.profileData

  // State
  const [selectedPurposes, setSelectedPurposes] = useState<UsagePurpose[]>(savedPurposesFromState || [])
  const [focusedIndex, setFocusedIndex] = useState(0)
  /*
    Ob schon in die Karten geklickt wurde (#147). Bis dahin trägt die Karte mit Fokus ihren Ring
    immer — wer per Mausklick auf die Seite kam, sah sonst nicht, dass die Tastatur schon in den
    Karten steht (`:focus-visible` greift nach einem Klick nicht). Danach nur noch per Tastatur,
    damit eine angeklickte Karte keinen zweiten Rahmen bekommt.
  */
  const [pointerUsed, setPointerUsed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Refs
  const firstCardRef = useRef<HTMLButtonElement>(null)

  // Platform-spezifische CSS-Klasse
  const platformClass = platformAPI.platform === 'electron' ? 'setup-purpose-page--electron' : 'setup-purpose-page--web'

  /**
   * Auto-focus effect
   * Sets keyboard focus on the first card when page loads
   */
  useEffect(() => {
    // Track page view (only for cloud users)
    const loginMethod = localStorage.getItem('loginMethod')
    if (loginMethod !== 'offline') {
      void account.updateSetupProgress('purpose')
    }

    if (firstCardRef.current) {
      firstCardRef.current.focus()
    }
  }, [account])

  /**
   * Toggles selection state of a usage purpose card
   * @param purposeId - The ID of the purpose to toggle
   */
  const togglePurpose = (purposeId: UsagePurpose) => {
    setSelectedPurposes(prev => {
      if (prev.includes(purposeId)) {
        return prev.filter(id => id !== purposeId)
      } else {
        return [...prev, purposeId]
      }
    })
  }

  /**
   * Handles keyboard navigation for the 2x3 grid of cards
   *
   * @param e - Keyboard event
   * @param purposeId - The ID of the current card
   * @param index - The index of the current card (0-5)
   *
   * @remarks
   * Grid Layout (2 columns x 3 rows):
   * [0] [1]
   * [2] [3]
   * [4] [5]
   *
   * Supported Keys:
   * - Space: Toggle current card
   * - Enter: Continue to home
     * - Arrow Right/Left: Move horizontally
   * - Arrow Up/Down: Move vertically (±2 positions)
   */
  const handleCardKeyDown = (e: React.KeyboardEvent, purposeId: UsagePurpose, index: number) => {
    const COLUMNS = 2 // Grid has 2 columns

    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      togglePurpose(purposeId)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleContinue()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      const nextIndex = (index + 1) % USAGE_PURPOSE_OPTIONS.length
      setFocusedIndex(nextIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-purpose-page__card')[nextIndex]?.focus()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevIndex = (index - 1 + USAGE_PURPOSE_OPTIONS.length) % USAGE_PURPOSE_OPTIONS.length
      setFocusedIndex(prevIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-purpose-page__card')[prevIndex]?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      // Move down one row (+ COLUMNS positions)
      const nextIndex = (index + COLUMNS) % USAGE_PURPOSE_OPTIONS.length
      setFocusedIndex(nextIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-purpose-page__card')[nextIndex]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      // Move up one row (- COLUMNS positions)
      const prevIndex = (index - COLUMNS + USAGE_PURPOSE_OPTIONS.length) % USAGE_PURPOSE_OPTIONS.length
      setFocusedIndex(prevIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-purpose-page__card')[prevIndex]?.focus()
    }
  }

  /**
   * Handles continue button click
   * Saves selected purposes to Supabase (online mode) or just navigates (offline mode)
   */
  const handleContinue = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      // Check if offline mode
      const loginMethod = localStorage.getItem('loginMethod')
      const isOfflineMode = loginMethod === 'offline'

      if (isOfflineMode) {
        // Offline mode - save to Dexie

        try {
          // Update existing offline profile with purposes
          const updated = await account.updateOfflineProfile({
            usagePurposes: selectedPurposes,
            updatedAt: new Date(),
            lastModified: new Date()
          })
          if (!updated) {
            console.warn('[SetupPurpose] No offline profile found')
          }
        } catch (error) {
          console.error('[SetupPurpose] Error saving to Dexie:', error)
        }

        navigate('/setup-intent', {
          state: {
            savedPurposes: selectedPurposes,
            savedIntents: savedIntentsFromState,
            profileData: profileDataFromState
          }
        })
        return
      }

      // Online mode - save to the account
      const user = await account.getCloudUser()
      if (!user) {
        console.error('[SetupPurpose] No user found')
        navigate('/home')
        return
      }

      // Save intentions (even if empty array)
      const result = await account.saveIntentions(user.id, {
        usagePurposes: selectedPurposes
      })

      if (result.success) {
        navigate('/setup-intent', {
          state: {
            savedPurposes: selectedPurposes,
            savedIntents: savedIntentsFromState,
            profileData: profileDataFromState
          }
        })
      } else {
        console.error('[SetupPurpose] Save error:', result.error)
        // Navigate anyway, don't block user
        navigate('/setup-intent', {
          state: {
            savedPurposes: selectedPurposes,
            savedIntents: savedIntentsFromState,
            profileData: profileDataFromState
          }
        })
      }
    } catch (error) {
      console.error('[SetupPurpose] Unexpected error:', error)
      navigate('/setup-intent')
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Skips the question: on to the intent page without saving purposes, but
   * with the setup data, so "Back" there still knows the profile.
   */
  const handleSkip = () => {
    if (isSubmitting) return
    navigate('/setup-intent', {
      state: {
        savedPurposes: selectedPurposes,
        savedIntents: savedIntentsFromState,
        profileData: profileDataFromState
      }
    })
  }

  /**
   * Go back to profile page
   */
  const handleBack = () => {
    navigate('/setup-profile', {
      state: {
        savedPurposes: selectedPurposes,
        savedIntents: savedIntentsFromState,
        profileData: profileDataFromState
      }
    })
  }

  return (
    <div
      className={`setup-purpose-page ${platformClass}${pointerUsed ? ' setup-purpose-page--pointer' : ''}`}
      // Escape überspringt die Frage, wo auch immer der Fokus steht — das
      // ESC-Kürzel am Überspringen-Knopf verspricht genau das.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          handleSkip()
        }
      }}
    >
      {/* Scrollable Container */}
      <div className="setup-purpose-page__scrollable-container">
        <div className="setup-purpose-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-purpose-page__content-area">

            {/* Left Side - Welcome */}
            <div className="setup-purpose-page__left-section">
              <div className="setup-purpose-page__left-content">
                <div className="setup-purpose-page__icon">
                  <BackpackIcon />
                </div>
                <h1 className="setup-purpose-page__title">
                  {t('setupPurpose.title')}
                </h1>
                {/* Lokal und mit Konto derselbe Text: Die Antworten gehen in beiden
                    Fällen raus (lokal anonym, seit 25.09.2026). */}
                <p className="setup-purpose-page__subtitle">
                  {t('setupPurpose.subtitle')}
                </p>
              </div>
            </div>

            {/* Right Side - Cards */}
            <div className="setup-purpose-page__right-section">
              {/* Dynamic Prompt Text */}
              <p className="setup-purpose-page__prompt">
                {selectedPurposes.length > 0
                  ? t('setupPurpose.selected', { count: selectedPurposes.length })
                  : t('setupPurpose.selectPrompt')}
              </p>

              {/* Grid of Cards */}
              <div className="setup-purpose-page__grid" onPointerDown={() => setPointerUsed(true)}>
                {USAGE_PURPOSE_OPTIONS.map((option, index) => {
                  const isSelected = selectedPurposes.includes(option.id)
                  const isFocused = focusedIndex === index

                  return (
                    <button
                      key={option.id}
                      ref={index === 0 ? firstCardRef : null}
                      onClick={() => togglePurpose(option.id)}
                      onKeyDown={(e) => handleCardKeyDown(e, option.id, index)}
                      onFocus={() => setFocusedIndex(index)}
                      className={`setup-purpose-page__card ${
                        isSelected ? 'setup-purpose-page__card--selected' : ''
                      } ${
                        isFocused ? 'setup-purpose-page__card--focused' : ''
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className="setup-purpose-page__card-emoji">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M29.96 2.27C28.26 0 20.13 0 20.13 0H16H11.87C11.87 0 3.74 0 2.04 2.27C0.41 4.45 0 7.14 0 16C0 24.86 0.41 27.55 2.04 29.73C3.74 32 11.87 32 11.87 32H16H20.13C20.13 32 28.26 32 29.96 29.73C31.59 27.55 32 24.86 32 16C32 7.14 31.59 4.45 29.96 2.27Z" fill={option.bgColor}/>
                        </svg>
                        <span className="setup-purpose-page__card-emoji-icon">{option.emoji}</span>
                      </span>
                      <span className="setup-purpose-page__card-label">
                        {t(option.labelKey)}
                      </span>
                    </button>
                  )
                })}
              </div>
              <CardKeysHint />
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar — ohne `disabled` während des kurzen Speicherns (#321): Das Abdunkeln blitzte vor dem
          Seitenwechsel auf. Gesperrt ist trotzdem, `handleContinue` und `handleSkip` prüfen `isSubmitting`. */}
      <div className="setup-purpose-page__action-bar">
        <Button
          variant="tertiary"
          onClick={handleSkip}
          shortcut={{ kind: 'character', text: 'ESC' }}
        >
          {t('setupPurpose.skip')}
        </Button>
        <div className="setup-purpose-page__action-bar-content">
          <Button
            variant="secondary"
            onClick={handleBack}
          >
            {t('setupPurpose.back')}
          </Button>
          <Button
            onClick={handleContinue}
            shortcut={{ kind: 'return' }}
          >
            {t('setupPurpose.continue')}
          </Button>
        </div>
      </div>
    </div>
  )
}
